import { Children } from "react";

import { Box, Stack, useTheme } from "@mui/material";

import { FormDateSelectFieldProps } from "./types";

import { FormSelectField } from "@/forms-design-system/FormSelectField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { mergeSx } from "@/forms-design-system/helpers";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useTranslate } from "@/hooks/useTranslate";

export function FormDateSelectField({
  id,
  disabled,
  timezone,
  count,
  placeholder,
  children,
  sx,
  components,
  control,
  name,
  ...rest
}: FormDateSelectFieldProps) {
  const theme = useTheme();
  const { t } = useTranslate();
  if (
    !theme.palette.forms ||
    !theme.palette.formsComponents?.selectField ||
    !theme.palette.formsComponents?.dateSelectField
  ) {
    return <InvalidThemeErrorMessage />;
  }

  return (
    <Box sx={sx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        padding={theme.spacing(1, 1.5)}
        gap={{ sm: 2 }}
        sx={{
          backgroundColor: theme.palette.formsComponents.dateSelectField.labelBackgroundColor,
          borderStartStartRadius: theme.radius(1),
          borderStartEndRadius: theme.radius(1),
        }}
      >
        <Box
          id={id}
          component="label"
          htmlFor={id}
          color={theme.palette.formsComponents.dateSelectField.labelTextColor}
          sx={mergeSx(
            {
              ...theme.typography.subtitle1,
              cursor: disabled ? "default" : "pointer",
            },
            components?.label?.sx
          )}
        >
          {t("common", "dateSelectLabel")}
        </Box>
        <FormTypography
          color={theme.palette.formsComponents.dateSelectField.labelTextColor}
          variant="body2"
          sx={components?.timezone?.sx}
        >
          {t("common", "dateSelectTimezoneLabel", { timezone })}
        </FormTypography>
      </Stack>
      <Box
        padding={theme.spacing(1.5, 1)}
        sx={{
          border: `1px solid ${theme.palette.formsComponents.dateSelectField.inputContainerBorderColor}`,
          borderBlockStart: "none",
          borderEndStartRadius: theme.radius(1),
          borderEndEndRadius: theme.radius(1),
        }}
      >
        <FormSelectField
          id={id}
          required
          disabled={disabled}
          placeholder={
            placeholder ?? t("common", "dateSelectPlaceholder", { count: count ?? Children.count(children) })
          }
          data-test={components?.select?.["data-test"]}
          sx={components?.select?.sx}
          control={control}
          name={name}
          {...rest}
        >
          {children}
        </FormSelectField>
      </Box>
    </Box>
  );
}
